/**
 * Monument/Product Management Module
 */
import Konva from "konva";
import { getProductById } from "./utils.js";

export class MonumentManager {
	constructor(stageManager, appState) {
		this.stageManager = stageManager;
		this.appState = appState;
		this.currentCandleId = null;
	}

	/**
	 * Load monument image by product ID
	 */
	loadMonumentImage(productId) {
		const product =
			getProductById(productId) ||
			(window.MC_DATA.products && window.MC_DATA.products[0]) ||
			null;

		return this.loadBackgroundImage(product ? product.image : null);
	}

	loadCustomMonumentImage(imageSrc) {
		return this.loadBackgroundImage(imageSrc, "custom monument");
	}

	loadBackgroundImage(imageSrc, contextLabel = "monument") {
		const bgLayer = this.stageManager.getBgLayer();

		bgLayer.destroyChildren();
		bgLayer.draw();

		if (!imageSrc) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			const imageObj = new window.Image();
			imageObj.crossOrigin = "anonymous";

			imageObj.onload = () => {
				const stage = this.stageManager.getStage();
				const imgWidth = imageObj.width;
				const imgHeight = imageObj.height;

				const scale = Math.min(
					stage.width() / imgWidth,
					stage.height() / imgHeight
				);

				const konvaImage = new Konva.Image({
					image: imageObj,
					width: imgWidth * scale,
					height: imgHeight * scale,
					x: stage.width() / 2 - (imgWidth * scale) / 2,
					y: stage.height() / 2 - (imgHeight * scale) / 2,
					listening: false,
				});

				bgLayer.add(konvaImage);
				bgLayer.draw();
				resolve();
			};

			imageObj.onerror = (error) => {
				console.error(
					`Failed to load ${contextLabel} image:`,
					error,
					imageSrc
				);
				resolve();
			};

			imageObj.src = imageSrc;
		});
	}

	/**
	 * Render products list
	 */
	renderProductsList() {
		const $list = jQuery(".mc-monuments-list");
		if (!$list.length) return;

		$list.empty();

		if (!window.MC_DATA.products || !window.MC_DATA.products.length) {
			$list.append("<p>Nerasta paminklų šioje kategorijoje.</p>");
			return;
		}

		window.MC_DATA.products.forEach((product) => {
			const $item = jQuery('<div class="mc-monuments-list-item"></div>')
				.attr("data-product-id", product.id)
				.append(
					jQuery("<img>")
						.attr("src", product.image || "")
						.attr("alt", product.title),
					jQuery('<div class="mc-monuments-title"></div>').text(
						product.title
					)
				);
			$list.append($item);
		});
	}

	/**
	 * Highlight selected product
	 */
	highlightSelectedProduct(productId) {
		jQuery(".mc-monuments-list .mc-monuments-list-item").removeClass(
			"mc-selected"
		);
		if (productId) {
			jQuery(
				'.mc-monuments-list .mc-monuments-list-item[data-product-id="' +
					productId +
					'"]'
			).addClass("mc-selected");
		}
	}

	getCandleById(productId) {
		const candles = (window.MC_DATA && window.MC_DATA.candles) || [];
		return candles.find((product) => product.id === productId);
	}

	renderCandlesList() {
		const $list = jQuery(".mc-candles-list");
		if (!$list.length) return;

		$list.empty();
		const candles = (window.MC_DATA && window.MC_DATA.candles) || [];

		if (!candles.length) {
			$list.append(
				"<p>Nerasta vazų šiai kategorijai. Patikrinkite produktų nustatymus.</p>"
			);
			return;
		}

		candles.forEach((product) => {
			const $item = jQuery(
				'<div class="mc-monuments-list-item mc-candles-list-item"></div>'
			)
				.attr("data-product-id", product.id)
				.append(
					jQuery("<img>")
						.attr("src", product.thumb || product.image || "")
						.attr("alt", product.title),
					jQuery('<div class="mc-monuments-title"></div>').text(
						product.title
					)
				);
			$list.append($item);
		});
	}

	highlightSelectedCandle(productId) {
		jQuery(".mc-candles-list-item").removeClass("mc-selected");
		jQuery(
			'.mc-candles-list-item[data-product-id="' + productId + '"]'
		).addClass("mc-selected");
		this.currentCandleId = productId;
	}

	addCandleToCanvas(productId) {
		const product = this.getCandleById(productId);
		if (!product || !product.image) return;

		const stage = this.stageManager.getStage();
		const mainLayer = this.stageManager.getMainLayer();
		const transformer = this.stageManager.getTransformer();

		const img = new window.Image();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			const maxW = stage.width() * 0.25;
			const baseScale = img.width > maxW ? maxW / img.width : 1;
			const scale = baseScale * 0.8;
			const dispW = img.width * scale;
			const dispH = img.height * scale;

			const node = new Konva.Image({
				image: img,
				x: stage.width() / 2 - dispW / 2,
				y: stage.height() / 2 - dispH / 2,
				width: dispW,
				height: dispH,
				draggable: true,
				name: "mc-decor",
				decorTitle: product.title || "",
				decorType: "candle",
				productId: product.id,
			});

			node.setAttr("initialWidth", dispW);
			node.setAttr("initialHeight", dispH);
			node.setAttr("baseScaleX", 1);
			node.setAttr("baseScaleY", 1);
			node.setAttr("baseRotation", 0);
			node.setAttr("baseX", node.x());
			node.setAttr("baseY", node.y());

			mainLayer.add(node);
			mainLayer.draw();

			const selectNode = (evt) => {
				if (window.mcSetActiveElement) {
					window.mcSetActiveElement(node, {
						multiSelect: this.isMultiSelectGesture(evt),
					});
				}

				this.stageManager.setPhotosNotActive();

				transformer.nodes([node]);
				transformer.keepRatio(true);
				transformer.enabledAnchors([
					"top-left",
					"top-right",
					"bottom-right",
					"bottom-left",
				]);
				mainLayer.draw();
			};

			const handleDragStart = () => {
				jQuery(".mc-element-toolbar[data-mode='image']").hide();
			};

			selectNode();

			node.on("click tap", selectNode);
			node.on("dragstart", handleDragStart);
			node.on("dragend", selectNode);
		};

		img.onerror = () => {
			console.error("Nepavyko įkelti vazos paveikslėlio", product);
		};

		img.src = product.image;
	}

	isMultiSelectGesture(evt) {
		const nativeEvent = evt && evt.evt ? evt.evt : evt;
		if (!nativeEvent) return false;
		return Boolean(
			nativeEvent.shiftKey || nativeEvent.metaKey || nativeEvent.ctrlKey
		);
	}
}
